import { useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

import { usePixiApp } from '../../services/pixiApp/pixiAppProvider';
import { useApi } from '../../services/api/ApiProvider';
import { fetchShopItems, claimShopItem, fetchWallet } from '../../services/api/apiService';

import ShopManager from '../../features/shop/managers/shopManager';
import CollectionManager from '../../features/collection/managers/collectionManager';
import LoadingSpinnerManager from '../../features/common/managers/LoadingSpinnerManager';

const ShopPage = () => {
    const { app, setAutoResize } = usePixiApp();
    const { fetchDecks, fetchCollection, setCoins, setShopItems, hasFreeShopItem } = useApi();
    const navigate = useNavigate();

    const pixiContainerRef = useRef(null);
    const shopManagerRef = useRef(null);
    const loadingSpinnerManagerRef = useRef(null);
    const hasFreeShopItemRef = useRef(hasFreeShopItem);
    hasFreeShopItemRef.current = hasFreeShopItem;

    const goBack = () => {
        navigate('/lobby');
    };

    const goToCollection = async () => {
        loadingSpinnerManagerRef.current.attachToPixiApp(app);
        await CollectionManager.loadAssets();
        navigate('/collection');
    };

    const handleClaim = async (itemId) => {
        if (!shopManagerRef.current) return;

        shopManagerRef.current.setClaimingItemId(itemId);
        loadingSpinnerManagerRef.current?.attachToPixiApp(app);

        try {
            const result = await claimShopItem(itemId);
            const items = await fetchShopItems();
            await ShopManager.loadItemAssets(items);
            shopManagerRef.current.setItems(items);
            setShopItems(items);
            shopManagerRef.current.setCoins(result.coins);
            setCoins(result.coins);
            await Promise.all([fetchDecks(), fetchCollection()]);

            // the spinner stays up until the animator has its assets and takes over the screen
            await shopManagerRef.current.showOpenedCards(result.openedCards, itemId, () => {
                loadingSpinnerManagerRef.current?.detachFromPixiApp(app);
            });
        } catch (error) {
            console.error('Failed to claim shop item:', error);
            alert(error.response?.data?.error || error.message || 'Failed to claim item');
        } finally {
            loadingSpinnerManagerRef.current?.detachFromPixiApp(app);
            if (shopManagerRef.current) {
                shopManagerRef.current.setClaimingItemId(null);
            }
        }
    };

    useEffect(() => {
        let cancelled = false;

        const onAppResize = () => {
            if (shopManagerRef.current) {
                shopManagerRef.current.buildMainLayout(app.screen.width, app.screen.height);
            }
        };

        const initShopPage = async () => {
            if (!app || !pixiContainerRef.current) return;

            pixiContainerRef.current.addEventListener('touchstart', (e) => e.preventDefault(), { passive: false });

            const shopManager = new ShopManager();
            const loadingSpinnerManager = new LoadingSpinnerManager(app.screen.width, app.screen.height);
            shopManagerRef.current = shopManager;
            loadingSpinnerManagerRef.current = loadingSpinnerManager;

            const shopDataPromise = Promise.all([fetchShopItems(), fetchWallet()]);

            await ShopManager.loadStaticAssets();
            if (cancelled) return;

            shopManager.attachToPixiApp(app);
            pixiContainerRef.current.appendChild(app.canvas);
            shopManager.setShopBadgeVisible(hasFreeShopItemRef.current);
            shopManager.buildMainLayout(app.screen.width, app.screen.height);
            shopManager.onBackButtonClick = goBack;
            shopManager.onGoToCollectionButtonClick = goToCollection;
            shopManager.onClaimItem = handleClaim;
            window.addEventListener('appResize', onAppResize);

            loadingSpinnerManager.attachToPixiApp(app);

            try {
                const [items, wallet] = await shopDataPromise;
                if (cancelled) return;

                await ShopManager.loadItemAssets(items);
                if (cancelled) return;

                shopManager.setItems(items);
                setShopItems(items);
                shopManager.setCoins(wallet.coins);
            } catch (error) {
                console.error('Failed to load shop:', error);
            } finally {
                if (!cancelled && loadingSpinnerManager.container.parent) {
                    loadingSpinnerManager.detachFromPixiApp(app);
                }
            }
        };

        setAutoResize(true);
        initShopPage();

        return () => {
            cancelled = true;
            if (shopManagerRef.current?.container?.parent && app) {
                shopManagerRef.current.detachFromPixiApp(app);
            }
            if (loadingSpinnerManagerRef.current?.container?.parent && app) {
                loadingSpinnerManagerRef.current.detachFromPixiApp(app);
            }
            window.removeEventListener('appResize', onAppResize);
            setAutoResize(false);
        };
    }, [app]);

    useEffect(() => {
        shopManagerRef.current?.setShopBadgeVisible(hasFreeShopItem);
    }, [hasFreeShopItem]);

    return (
        <div style={{ width: '100dvw', height: '100dvh', overflow: 'hidden' }} ref={pixiContainerRef} />
    );
};

export default ShopPage;
